import { closingColumns } from "./closing-columns";
import { formatBucketLabel } from "./bucket-label";
import type {
  ClosingGridMode,
  ProductionReportResponse,
  ReportComparison,
  ReportComparisonColumn,
  ReportGroupBy,
  ReportStage,
} from "@/types/production-report.types";
import { GROUP_BY_LABELS } from "@/types/production-report.types";

// Spec 083 (T037) — a exportação do fechamento.
//
// Do lado do navegador, sobre a resposta que a tela já tem: o que o gestor quer exportar é **o que
// está na tela**, incluindo o modo da grade e o agrupamento escolhidos. Gerar no servidor exigiria
// repetir a decisão de colunas dos dois lados, e o primeiro desencontro sairia num arquivo que
// ninguém confere de novo.
//
// Espelha `features/dashboard/lib/dashboard-export.ts`, inclusive o BOM — sem ele o Excel em pt-BR
// abre acento como caractere quebrado.

const DECIMAL = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
  useGrouping: false,
});

/** Ponto e vírgula, porque a vírgula é separador decimal em pt-BR. */
function escape(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function stageRows(
  stage: ReportStage,
  groupBy: ReportGroupBy,
  mode: ClosingGridMode,
  heroId: string | null
): string[] {
  // O MESMO indicador eleito da tela. Fixar o primeiro aqui fazia "Saída útil", "Quebra" e a coluna
  // de percentual saírem de um indicador diferente do que o gestor acabou de conferir — um arquivo
  // que discorda da tela é pior que nenhum arquivo.
  const cols = closingColumns(stage, mode, heroId);
  const header = ["Linha", "Período", "Parcial", ...cols.map((c) => c.label)];
  const rows = stage.buckets.map((b) =>
    [
      stage.lineName,
      formatBucketLabel(b, groupBy),
      b.partial ? "sim" : "não",
      ...cols.map((c) => {
        const v = c.value(b);
        return v === null ? "" : DECIMAL.format(v);
      }),
    ]
      .map(escape)
      .join(";")
  );
  return [header.map(escape).join(";"), ...rows];
}

/**
 * Spec 084 (FR-017c) — o cabeçalho de METADADOS, antes do primeiro bloco.
 *
 * Um arquivo recortado que não diz que é recortado vira, na semana seguinte, "o relatório do mês" —
 * e ninguém tem como perceber que faltam duas variedades ali dentro.
 */
function metadataRows(report: ProductionReportResponse): string[] {
  const linhas = [
    ["Período", `${report.period.from} a ${report.period.to}`],
    ["Agrupamento", GROUP_BY_LABELS[report.period.groupBy] ?? report.period.groupBy],
    [
      "Recorte",
      report.filter
        ? `${report.filter.characteristicName}: ${report.filter.valueLabel}`
        : "sem recorte",
    ],
    ["Quadro comparativo", report.comparison ? report.comparison.characteristicName : "não"],
  ];
  if (report.filter) {
    linhas.push([
      "Fora do recorte",
      `${report.filter.excluded.noneRuns} sem valor · ${report.filter.excluded.mixedRuns} mista(s)`,
    ]);
  }
  return linhas.map((l) => l.map(escape).join(";"));
}

type ColumnStage = ReportComparisonColumn["stages"][number];

const num = (v: number | null | undefined): string =>
  v === null || v === undefined ? "" : DECIMAL.format(v);

/** Ausência continua ausência também no arquivo: célula VAZIA, nunca `0`. */
const soma = (itens: Array<{ quantity: number }> | undefined): string =>
  itens === undefined || itens.length === 0
    ? ""
    : DECIMAL.format(itens.reduce((acc, i) => acc + i.quantity, 0));

function comparisonStageRows(
  comparison: ReportComparison,
  stage: ReportStage,
  colunas: ReportComparisonColumn[]
): string[] {
  const celulas = colunas.map((c) => c.stages.find((s) => s.lineId === stage.lineId));
  const linha = (rotulo: string, valor: (s: ColumnStage | undefined) => string) =>
    [rotulo, ...celulas.map(valor)].map(escape).join(";");
  return [
    escape(`${stage.lineName} — por ${comparison.characteristicName}`),
    ["Item", ...colunas.map((c) => c.label)].map(escape).join(";"),
    ["Bateladas", ...colunas.map((c) => String(c.runs))].map(escape).join(";"),
    linha("Entrada", (s) => num(s?.input.quantity)),
    linha("Saída útil", (s) => soma(s?.products)),
    linha("Perdas", (s) => soma(s?.losses)),
    ...indicadoresDoQuadro(colunas, stage.lineId).map((ind) =>
      linha(ind.name, (s) => num(s?.indicators.find((i) => i.id === ind.id)?.value))
    ),
  ];
}

/** O quadro sai como um bloco por etapa, com uma coluna por valor — o mesmo que está na tela. */
function comparisonRows(comparison: ReportComparison, report: ProductionReportResponse): string[] {
  const out: string[] = [];
  for (const stage of report.stages) {
    const colunas = comparison.columns.filter((c) =>
      c.stages.some((s) => s.lineId === stage.lineId)
    );
    if (colunas.length === 0) continue;
    out.push("", ...comparisonStageRows(comparison, stage, colunas));
  }
  return out;
}

function indicadoresDoQuadro(
  colunas: ReportComparisonColumn[],
  lineId: string
): Array<{ id: string; name: string }> {
  const vistos = new Map<string, string>();
  for (const c of colunas) {
    for (const i of c.stages.find((s) => s.lineId === lineId)?.indicators ?? []) {
      if (!vistos.has(i.id)) vistos.set(i.id, i.name);
    }
  }
  return [...vistos].map(([id, name]) => ({ id, name }));
}

export function buildReportCsv(
  report: ProductionReportResponse,
  mode: ClosingGridMode,
  heroId: string | null = null
): string {
  const linhas: string[] = [...metadataRows(report), ""];
  for (const stage of report.stages) {
    if (linhas.at(-1) !== "") linhas.push("");
    // A eleição é por etapa: um id que não existe nesta etapa cai no primeiro dela, como na grade.
    const hero =
      stage.indicators.find((i) => i.id === heroId)?.id ?? stage.indicators[0]?.id ?? null;
    linhas.push(...stageRows(stage, report.period.groupBy, mode, hero));
  }
  if (report.comparison) linhas.push(...comparisonRows(report.comparison, report));
  return linhas.join("\n");
}

export function downloadReportCsv(
  report: ProductionReportResponse,
  mode: ClosingGridMode,
  heroId: string | null = null
): void {
  const csv = buildReportCsv(report, mode, heroId);
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `controle-de-producao-${report.period.from}-a-${report.period.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
